import { useState } from 'react'
import { ExternalLink, Pencil, Plus, PowerOff } from 'lucide-react'
import { Link } from 'react-router'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useLetterTemplates, useRequestForm, useRequestTypeMutations, useRequestTypes } from '../api'
import type { RequestTypeView } from '../types'
import { Select } from '@/components/ui/select'

interface Draft {
  id?: string
  legalEntityId: string
  code: string
  name: string
  description: string
  category: string
  requiresAttachment: boolean
  slaHours: string
  letterTemplateId: string
  active: boolean
}

const EMPTY: Draft = {
  legalEntityId: '',
  code: '',
  name: '',
  description: '',
  category: '',
  requiresAttachment: false,
  slaHours: '',
  letterTemplateId: '',
  active: true,
}

/**
 * The catalogue of request types.
 *
 * <p>The form each type asks for is edited on the custom-fields screen, not here — they are
 * the same rows, and a second editor would be a second place for the same regex to be wrong.
 * This screen links there rather than duplicating it.
 */
export function RequestTypesPage() {
  useBreadcrumb()
  const types = useRequestTypes()
  const templates = useLetterTemplates()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { create, update, deactivate } = useRequestTypeMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toDeactivate, setToDeactivate] = useState<RequestTypeView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [inspecting, setInspecting] = useState<string | null>(null)

  const form = useRequestForm(inspecting ?? undefined)

  async function save() {
    if (!draft) return
    setError(null)
    const body = {
      legalEntityId: draft.legalEntityId || null,
      code: draft.code,
      name: draft.name,
      description: draft.description || null,
      category: draft.category || null,
      requiresAttachment: draft.requiresAttachment,
      slaHours: draft.slaHours === '' ? null : Number(draft.slaHours),
      letterTemplateId: draft.letterTemplateId || null,
      active: draft.active,
    }
    try {
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, ...body })
      } else {
        await create.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That request type could not be saved')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Request types"
        subtitle="What this company accepts a request for, and what each one asks."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            Define a type
          </Button>
        }
      />

      <Card>
        {types.isPending && <Skeleton className="m-4 h-32" />}
        {types.isError && <ErrorState error={types.error} onRetry={() => void types.refetch()} />}
        {types.isSuccess && types.data.length === 0 && (
          <EmptyState
            title="No request types yet"
            description="Define one, then design its form on the custom fields screen."
          />
        )}
        {(types.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Name</th>
                  <th scope="col" className="p-3 font-medium">Code</th>
                  <th scope="col" className="p-3 font-medium">Category</th>
                  <th scope="col" className="p-3 font-medium">Answers within</th>
                  <th scope="col" className="p-3 font-medium">Produces</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {types.data?.map((type) => (
                  <tr key={type.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">
                      <button
                        type="button"
                        className="underline-offset-2 hover:underline"
                        onClick={() => setInspecting(inspecting === type.id ? null : type.id)}
                      >
                        {type.name}
                      </button>
                      {inspecting === type.id && (
                        <p className="mt-1 text-xs font-normal text-[var(--color-muted)]">
                          {form.isPending
                            ? 'Loading the form…'
                            : (form.data?.length ?? 0) === 0
                              ? 'Asks for nothing beyond a subject.'
                              : `Asks for: ${form.data?.map((field) => field.label).join(', ')}`}
                        </p>
                      )}
                    </td>
                    <td className="p-3"><code className="text-xs">{type.code}</code></td>
                    <td className="p-3 text-[var(--color-muted)]">{type.category ?? '—'}</td>
                    <td className="p-3 tabular-nums">
                      {type.slaHours == null ? '—' : `${type.slaHours} h`}
                    </td>
                    <td className="p-3 text-[var(--color-muted)]">
                      {type.letterTemplateId
                        ? templates.data?.find((t) => t.id === type.letterTemplateId)?.name ?? 'A letter'
                        : 'A decision'}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={type.active ? 'ACTIVE' : 'INACTIVE'} />
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button" size="icon" variant="ghost"
                          aria-label={`Edit ${type.name}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: type.id,
                              legalEntityId: type.legalEntityId ?? '',
                              code: type.code,
                              name: type.name,
                              description: type.description ?? '',
                              category: type.category ?? '',
                              requiresAttachment: type.requiresAttachment,
                              slaHours: type.slaHours?.toString() ?? '',
                              letterTemplateId: type.letterTemplateId ?? '',
                              active: type.active,
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {type.active && (
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`Deactivate ${type.name}`}
                            onClick={() => setToDeactivate(type)}
                          >
                            <PowerOff className="size-4" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-sm text-[var(--color-muted)]">
        The fields each type asks for are custom fields, edited on{' '}
        <Link to="/settings/custom-fields" className="text-[var(--color-primary)] hover:underline">
          Settings → Custom fields
          <ExternalLink className="ms-1 inline size-3" aria-hidden="true" />
        </Link>
        . There is one definition of a form, and one validator behind it.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(next) => !next && setDraft(null)}
        title={draft?.id ? `Edit ${draft.name}` : 'Define a request type'}
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void save()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rt-name">Name</Label>
              <Input id="rt-name" required value={draft.name}
                     onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rt-code">Code</Label>
              <Input id="rt-code" required value={draft.code} disabled={draft.id != null}
                     placeholder="SALARY_ADVANCE"
                     onChange={(event) => setDraft({ ...draft, code: event.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rt-category">Category</Label>
              <Input id="rt-category" value={draft.category} placeholder="Finance, HR, IT…"
                     onChange={(event) => setDraft({ ...draft, category: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rt-sla">Answer within (hours)</Label>
              <Input id="rt-sla" type="number" min={1} value={draft.slaHours}
                     placeholder="No promise"
                     onChange={(event) => setDraft({ ...draft, slaHours: event.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rt-entity">Legal entity</Label>
              <Select id="rt-entity" value={draft.legalEntityId}
                      onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
                      className="w-full">
                <option value="">Every entity</option>
                {(entities.data ?? []).map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rt-letter">Issues a letter on approval</Label>
              <Select id="rt-letter" value={draft.letterTemplateId}
                      onChange={(event) => setDraft({ ...draft, letterTemplateId: event.target.value })}
                      className="w-full">
                <option value="">No — it is a decision and nothing else</option>
                {(templates.data ?? []).map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="rt-description">Description</Label>
              <Input id="rt-description" value={draft.description}
                     onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
            </div>

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={draft.requiresAttachment}
                     onChange={(event) => setDraft({ ...draft, requiresAttachment: event.target.checked })} />
              A document must be attached
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={draft.active}
                     onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
              Accepting new requests
            </label>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toDeactivate != null}
        onOpenChange={(next) => !next && setToDeactivate(null)}
        title={`Stop accepting ${toDeactivate?.name ?? ''}?`}
        description="Requests already raised are kept and can still be decided. No new ones will be accepted."
        confirmLabel="Deactivate"
        onConfirm={() => {
          if (toDeactivate) {
            deactivate.mutate(toDeactivate.id, { onSettled: () => setToDeactivate(null) })
          }
        }}
      />
    </div>
  )
}
